// THE LEVIATHAN, the extraction pursuer (docs/BOSS_GRAMMAR.md §4b; Godot leviathan_pursuer.gd +
// leviathan_tendril.gd). Not a boss island: it is what comes for the posse while they hold the
// extraction platform. Every extraction, the moment the bell is rung, it rises out of the cloud
// sea beside the islet (G5 arrival: a 5 s rise, no attacks until it has settled) and looms there,
// face to the posse, until the train has gone.
//
// The demi-god posture (§4b): it vastly outclasses the posse, but it is a strippable damage map
// and the wound carries across every extraction of the run (WOUNDS, below), so a posse that keeps
// riding deeper can chip it to death across a playthrough. Kill it and THE CYCLE IS BROKEN: the
// pressure stops and it never comes back this run. No boss bar, no coaching (a killable secret).
//
// Parts (Assets/bosses/leviathan_body + leviathan_tendril, tools/boss_forge/leviathan.py names):
//   hull            AV4 clang (×0.3): deflected is obvious. SKINNED (all five tendrils gone):
//                   AV1 and the four fx_wound ember cracks open as gold crits.
//   eye_L / eye_R   GOLD (×1.5) porthole eyes; a hit snaps the lid_* visor shut for 1.4 s (AV4)
//   maw / jaw       AV2 (×0.6) shut; while the jaw hangs open (the BELCH) the furnace is GOLD ×1.4
//   turret_*        4 blaster balls + 2 rocket drums, AV1, 400 each: shoot them off, they stop
//   barnacle_*      6 cosmetic clumps, AV0, 150 each: pop-off spectacle
//   tendrils        5 piston limbs, AV1, one 2,400 pool each (shaft + head); the head's
//                   wp_head_core is the always-on GOLD path (×1.25). Empty → SEVERED, and the
//                   sock_tendril_* socket is an open gold wound for 4 s.
//   Every pool forwards 1:1 into the body (the one Enemy whose hp the posse is really draining).
//
// The beat (G1-G3: one major action at a time, locked telegraphs, owed recovery):
//   ROCKET BARRAGE  1.2 s tell (drums glow) → bombs lobbed at points locked at the tell (the
//                   client's bomb markers are the ground channel) → 1.4 s recover
//   FURNACE BELCH   1.3 s tell (the jaw drops: the furnace is gold) → three boulders → the jaw
//                   hangs open 2 s (the punish window)
//   TENDRIL SLAM    1.4 s tell (the limb rears, a strike ring locks on the deck) → the whip →
//                   40 + an INBOARD shove, once per cowboy → the head lies on the deck 1.6 s
//                   (its gold core in point-blank range), then retracts
//   minor pressure  the front blaster turrets plink slow bolts, paused during every tell/recover
// G4: everything ≤ 40 per hit; nothing lands without a tell; shoves push inboard, never off.

import { DT, Rng, clamp, hash32, len2, secs, smoothstep, wrapAngle } from '../core';
import type { Reader, Writer } from '../codec';
import { Ev } from '../codec';
import { EType } from '../defs';
import { BKind, lobVelocity, BULLETS } from '../bullets';
import { EState, Enemy, historyAt, stepStatus } from '../enemies';
import { Phase, PlayerState, isTargetable } from '../player';
import { NO_GROUND } from '../world';
import type { Game } from '../game';
import { BossBase, BossBeat, BossDef, BossHit, BossKind, BossNet, Ray } from './types';

// ------------------------------------------------------------------ the creature, in model space
// GLB frame (metres, Y up, the face looks down +Z), drawn at LEVI_SCALE. Measured from the shipped
// GLBs' node transforms (node -e dump over Assets/bosses/leviathan_*/); keep in sync on a reforge.
/** ~92 m across the skirt, ~100 m crown to keel, tendrils another ~91 m: island-sized */
export const LEVI_SCALE = 1.5;
type V3 = readonly [number, number, number];
export const EYES: { p: V3; ax: V3 }[] = [
  { p: [-11.13, 9.75, 22.82], ax: [-0.41, 0.36, 0.84] },
  { p: [11.13, 9.75, 22.82], ax: [0.41, 0.36, 0.84] },
];
/** blaster 0..3 then rocket 0..1: mount point, surface normal (local +Y), muzzle (model space) */
export const TURRETS: { p: V3; ax: V3; mz: V3; rocket: boolean }[] = [
  { p: [-15.85, 16.87, 12.38], ax: [-0.6, 0.64, 0.47], mz: [-25.29, 20.61, 19.76], rocket: false },
  { p: [15.85, 16.87, 12.38], ax: [0.6, 0.64, 0.47], mz: [25.29, 20.61, 19.76], rocket: false },
  { p: [-18.9, 10.68, -14.76], ax: [-0.72, 0.41, -0.56], mz: [-28.78, 10.96, -22.49], rocket: false },
  { p: [18.9, 10.68, -14.76], ax: [0.72, 0.41, -0.56], mz: [28.78, 10.96, -22.49], rocket: false },
  { p: [-13.83, 22.26, 1.45], ax: [-0.53, 0.85, 0.06], mz: [-19.94, 28.55, 2.1], rocket: true },
  { p: [13.83, 22.26, 1.45], ax: [0.53, 0.85, 0.06], mz: [19.94, 28.55, 2.1], rocket: true },
];
export const BARNACLES: V3[] = [[5.44, -16.7, 20.28], [12.56, -19.44, 3.36], [14.85, -16.7, -14.85], [-3.36, -19.44, -12.56], [-20.28, -16.7, -5.44], [-9.19, -19.44, 9.19]];
export const WOUND_CRACKS: V3[] = [[-22.25, 3.69, 13.91], [22.38, 11.62, 8.15], [-13.42, 16.32, -16], [12.96, 5.51, -22.45]];
export const SOCKETS: V3[] = [[10.87, -21.3, 14.97], [17.59, -21.3, -5.72], [0, -21.3, -18.5], [-17.59, -21.3, -5.72], [-10.87, -21.3, 14.97]];
/** the throat (sock_maw region) and the lower mandible */
export const MAW: V3 = [0, -11, 29.5];
export const JAW: V3 = [0, -18.5, 29];
export const SOCK_MAW: V3 = [0, -11.84, 31.1];
/** the hull: dome + belly, two spheres */
const HULL: { c: V3; r: number }[] = [{ c: [0, 2, 0], r: 28 }, { c: [0, -12, 2], r: 22 }];
/** tendril: offset from joint k to joint k+1 (seg_00 → … → seg_11 → head), all along local -Y */
export const SEG_OFF = [8, 5, 6.2, 3.6, 6.8, 3.0, 4.4, 3.8, 5.0, 2.6, 3.6, 2.6];
/** hit radius of each segment (its mesh's half-width, plus a little so a 0.5 m piston rod is hittable) */
const SEG_R = [3.4, 2.0, 2.6, 1.6, 1.9, 1.5, 1.6, 1.4, 1.4, 1.3, 1.3, 1.3];
export const JOINTS = SEG_OFF.length; // 12 rotating joints: seg_00..seg_11
export const CORE_OFF = 6.11, HEAD_LEN = 13.0;
const REACH = SEG_OFF.reduce((a, b) => a + b, 0) + CORE_OFF; // 60.7 model m to the gold core
export const N_LIMBS = 5, N_TURRETS = 6, N_BARN = 6;

/** alive bits: limbs 0..4, turrets 5..10, barnacles 11..16 */
export const BIT_LIMB = 0, BIT_TURRET = 5, BIT_BARN = 11;
export const ALL_PARTS = (1 << 17) - 1;

// ------------------------------------------------------------------ tuning
/** the body's solo pool for the whole run (× (1 + 0.35 (n-1)) on its first summon) */
export const LEVI_HP = 12000;
export const LIMB_HP = 2400, TURRET_HP = 400, BARNACLE_HP = 150;
const RISE_SECS = 5, RISE_DEPTH = 150;
/** hull centre above the islet deck, and how far off the islet centre it stations */
export const HOVER = 20, STATION = 74, STATION_OUT = 8;
const BOB = 1.6;
const TURN = 0.18; // rad/s, the whole body swinging round to face the posse
const LOB_RANGE = 170, BOLT_RANGE = 60;
const SLAP_R = 6.5, SLAP_DMG = 40, SLAP_SHOVE = 16;
/** tell / execute / recover / retract seconds per attack (G2 floors: ranged ≥ 0.6, melee ≥ 0.8; G3 ≥ 0.9) */
const ATK_SECS: Record<number, number[]> = { 1: [1.2, 0.5, 1.4, 0], 2: [1.3, 0.5, 2.0, 0], 3: [1.4, 0.35, 1.6, 0.9] };

export const enum LAtk { None = 0, Barrage = 1, Belch = 2, Slap = 3 }
export const ATTACK_NAMES = ['', 'ROCKET BARRAGE', 'FURNACE BELCH', 'TENDRIL SLAM'];
export const enum LPh { Idle = 0, Tell = 1, Exec = 2, Recover = 3, Retract = 4 }
/** net.phase: low 3 bits LPh, then flags */
export const enum LBit { PhMask = 7, Skinned = 8, Rising = 16 }
/** this boss's own Ev.Boss beats */
export const enum LeviEv { PartLost = 16, Slam = 17, Skinned = 18, Flinch = 19, Volley = 20 }
/** PartLost a = kind */
export const enum LKind { Limb = 0, Turret = 1, Barnacle = 2 }

export const enum LPart { None = 0, Hull = 1, Eye = 2, Lid = 3, Maw = 4, Jaw = 5, Turret = 6, Barnacle = 7, Crack = 8, Socket = 9, Tendril = 10, Head = 11, Core = 12 }

// ------------------------------------------------------------------ the shared pose (server + client)
/** Everything the volumes and the limb poses depend on. Built from the server state (at a view
 *  tick) or from the decoded net payload + the interpolated body, so both sides agree. */
export interface LeviView {
  x: number; y: number; z: number; yaw: number;
  /** seconds since it arrived: the tendril wave clock */
  age: number;
  parts: number;
  /** lid shut bits (1 = L, 2 = R), severed-socket wound windows (bit per limb) */
  lids: number; socks: number;
  skinned: boolean;
  atk: number; ph: number; prog: number;
  limb: number; tx: number; ty: number; tz: number;
}
export const newLeviView = (): LeviView => ({ x: 0, y: 0, z: 0, yaw: 0, age: 0, parts: ALL_PARTS, lids: 0, socks: 0, skinned: false, atk: 0, ph: 0, prog: 0, limb: -1, tx: 0, ty: 0, tz: 0 });

export const limbAlive = (parts: number, i: number) => (parts & (1 << (BIT_LIMB + i))) !== 0;
export const turretAlive = (parts: number, i: number) => (parts & (1 << (BIT_TURRET + i))) !== 0;
export const barnAlive = (parts: number, i: number) => (parts & (1 << (BIT_BARN + i))) !== 0;
/** 0..1 how far the jaw hangs open (the belch: drops during the tell, hangs through the recover) */
export function jawOpen(v: { atk: number; ph: number; prog: number }) {
  if (v.atk !== LAtk.Belch) return 0;
  if (v.ph === LPh.Tell) return smoothstep(0, 0.6, v.prog);
  if (v.ph === LPh.Exec) return 1;
  if (v.ph === LPh.Recover) return v.prog < 0.85 ? 1 : 1 - (v.prog - 0.85) / 0.15;
  return 0;
}

/** model → world (scale, then yaw + π about Y, then the body position) */
export function toWorld(v: LeviView, mx: number, my: number, mz: number, out: { x: number; y: number; z: number }) {
  const a = v.yaw + Math.PI, c = Math.cos(a), s = Math.sin(a);
  const x = mx * LEVI_SCALE, z = mz * LEVI_SCALE;
  out.x = v.x + x * c + z * s; out.y = v.y + my * LEVI_SCALE; out.z = v.z - x * s + z * c;
  return out;
}
export function toModel(v: LeviView, wx: number, wy: number, wz: number, out: { x: number; y: number; z: number }) {
  const a = v.yaw + Math.PI, c = Math.cos(a), s = Math.sin(a);
  const px = wx - v.x, pz = wz - v.z;
  out.x = (c * px - s * pz) / LEVI_SCALE; out.y = (wy - v.y) / LEVI_SCALE; out.z = (s * px + c * pz) / LEVI_SCALE;
  return out;
}

// ---- tiny quaternion kit (x, y, z, w in a flat array at offset o)
function qSet(q: Float32Array | Float64Array, o: number, x: number, y: number, z: number, w: number) { q[o] = x; q[o + 1] = y; q[o + 2] = z; q[o + 3] = w; }
function qAxis(q: Float64Array, o: number, ax: number, ay: number, az: number, ang: number) {
  const h = ang * 0.5, s = Math.sin(h);
  qSet(q, o, ax * s, ay * s, az * s, Math.cos(h));
}
/** out = a ⊗ b (a then... standard Hamilton product: rotate by b, then by a) */
function qMul(a: Float64Array, ao: number, b: Float64Array, bo: number, out: Float64Array, oo: number) {
  const ax = a[ao], ay = a[ao + 1], az = a[ao + 2], aw = a[ao + 3];
  const bx = b[bo], by = b[bo + 1], bz = b[bo + 2], bw = b[bo + 3];
  out[oo] = aw * bx + ax * bw + ay * bz - az * by;
  out[oo + 1] = aw * by - ax * bz + ay * bw + az * bx;
  out[oo + 2] = aw * bz + ax * by - ay * bx + az * bw;
  out[oo + 3] = aw * bw - ax * bx - ay * by - az * bz;
}
/** rotate (vx, vy, vz) by q at o into out */
function qRot(q: Float64Array, o: number, vx: number, vy: number, vz: number, out: { x: number; y: number; z: number }) {
  const x = q[o], y = q[o + 1], z = q[o + 2], w = q[o + 3];
  const ix = w * vx + y * vz - z * vy, iy = w * vy + z * vx - x * vz, iz = w * vz + x * vy - y * vx, iw = -x * vx - y * vy - z * vz;
  out.x = ix * w + iw * -x + iy * -z - iz * -y;
  out.y = iy * w + iw * -y + iz * -x - ix * -z;
  out.z = iz * w + iw * -z + ix * -y - iy * -x;
  return out;
}
function qSlerp(a: Float64Array, ao: number, b: Float64Array, bo: number, t: number, out: Float64Array, oo: number) {
  let bx = b[bo], by = b[bo + 1], bz = b[bo + 2], bw = b[bo + 3];
  let d = a[ao] * bx + a[ao + 1] * by + a[ao + 2] * bz + a[ao + 3] * bw;
  if (d < 0) { d = -d; bx = -bx; by = -by; bz = -bz; bw = -bw; }
  let k0 = 1 - t, k1 = t;
  if (d < 0.9995) { const th = Math.acos(d), s = Math.sin(th); k0 = Math.sin((1 - t) * th) / s; k1 = Math.sin(t * th) / s; }
  const x = a[ao] * k0 + bx * k1, y = a[ao + 1] * k0 + by * k1, z = a[ao + 2] * k0 + bz * k1, w = a[ao + 3] * k0 + bw * k1;
  const l = Math.hypot(x, y, z, w) || 1;
  qSet(out, oo, x / l, y / l, z / l, w / l);
}
/** rotation whose columns are the basis (X, Y, Z) */
function qBasis(q: Float64Array, o: number, xx: number, xy: number, xz: number, yx: number, yy: number, yz: number, zx: number, zy: number, zz: number) {
  // m00 = xx, m01 = yx, m02 = zx / m10 = xy, m11 = yy, m12 = zy / m20 = xz, m21 = yz, m22 = zz
  const tr = xx + yy + zz;
  if (tr > 0) { const s = 0.5 / Math.sqrt(tr + 1); qSet(q, o, (yz - zy) * s, (zx - xz) * s, (xy - yx) * s, 0.25 / s); }
  else if (xx > yy && xx > zz) { const s = 2 * Math.sqrt(1 + xx - yy - zz); qSet(q, o, 0.25 * s, (yx + xy) / s, (zx + xz) / s, (yz - zy) / s); }
  else if (yy > zz) { const s = 2 * Math.sqrt(1 + yy - xx - zz); qSet(q, o, (yx + xy) / s, 0.25 * s, (zy + yz) / s, (zx - xz) / s); }
  else { const s = 2 * Math.sqrt(1 + zz - xx - yy); qSet(q, o, (zx + xz) / s, (zy + yz) / s, 0.25 * s, (xy - yx) / s); }
}

// scratch
const QA = new Float64Array(4), QB = new Float64Array(4), QC = new Float64Array(4), QI = new Float64Array(4), QR = new Float64Array(4);
const JA = new Float64Array(JOINTS * 2), JB = new Float64Array(JOINTS * 2);
const sv = { x: 0, y: 0, z: 0 }, sv2 = { x: 0, y: 0, z: 0 };

/** idle writhe for limb i: root quaternion → q (offset o), joint (bx, bz) → j */
function idlePose(i: number, age: number, q: Float64Array, o: number, j: Float64Array) {
  const s = SOCKETS[i], l = Math.hypot(s[0], s[2]) || 1, ox = s[0] / l, oz = s[2] / l;
  // splay outward from the belly, breathing
  qAxis(q, o, -oz, 0, ox, 0.34 + 0.07 * Math.sin(age * 0.5 + i * 1.3));
  j[0] = j[1] = 0;
  for (let k = 1; k < JOINTS; k++) {
    const amp = 0.07 + 0.007 * k;
    j[k * 2] = amp * Math.sin(age * 1.15 - k * 0.6 + i * 1.7);
    j[k * 2 + 1] = amp * 0.75 * Math.sin(age * 0.83 - k * 0.5 + i * 2.9);
  }
}

/** chord of the planar chain with uniform curl th per joint: (along, across) */
function chainChord(th: number, out: { x: number; y: number; z: number }) {
  let a = 0, b = 0;
  for (let k = 0; k < JOINTS; k++) {
    const L = SEG_OFF[k] + (k === JOINTS - 1 ? CORE_OFF : 0);
    a += L * Math.cos(k * th); b += L * Math.sin(k * th);
  }
  out.x = a; out.y = b;
  return out;
}

/** The strike pose for a limb reaching model point (tx, ty, tz): root → q@o, curl → th (returned).
 *  rear > 0 raises the whole limb by that angle and coils it (the tell). */
function aimPose(i: number, tx: number, ty: number, tz: number, rear: number, q: Float64Array, o: number) {
  const s = SOCKETS[i];
  let dx = tx - s[0], dy = ty - s[1], dz = tz - s[2];
  const dist = Math.hypot(dx, dy, dz) || 1;
  dx /= dist; dy /= dist; dz /= dist;
  // a = d × up (horizontal, ⟂ d): the whip arcs in the vertical plane through the target
  let ax = -dz, az = dx;
  const al = Math.hypot(ax, az);
  if (al < 1e-4) { ax = 1; az = 0; } else { ax /= al; az /= al; }
  // curl (negative: the arc rides above the chord and comes DOWN onto the target)
  let th = 0;
  if (dist < chainChord(0, sv).x) {
    let lo = -0.26, hi = 0;
    for (let it = 0; it < 22; it++) {
      const mid = (lo + hi) * 0.5;
      chainChord(mid, sv);
      if (Math.hypot(sv.x, sv.y) > dist) hi = mid; else lo = mid;
    }
    th = (lo + hi) * 0.5;
  }
  chainChord(th, sv);
  const alpha = Math.atan2(sv.y, sv.x) - rear;
  // start direction: d rotated by -alpha about a (toward a × d is +)
  const cx = 0 * dz - az * dy, cy = az * dx - ax * dz, cz = ax * dy - 0 * dx; // a × d
  const ca = Math.cos(alpha), sa = Math.sin(alpha);
  const sx = ca * dx - sa * cx, sy = ca * dy - sa * cy, sz = ca * dz - sa * cz;
  // basis: X = a, Y = -s, Z = X × Y
  const yx = -sx, yy = -sy, yz = -sz;
  const zx = 0 * yz - az * yy, zy = az * yx - ax * yz, zz = ax * yy - 0 * yx;
  qBasis(q, o, ax, 0, az, yx, yy, yz, zx, zy, zz);
  return rear > 0 ? th * 1.7 - 0.05 : th;
}

/** Local joint rotations of every limb (5 × 12 quaternions, x y z w): the client copies these onto
 *  the tendril bones, the server runs them through limbFK for the hit volumes. */
export function poseLimbs(v: LeviView, out: Float64Array) {
  const slapping = v.atk === LAtk.Slap && v.limb >= 0 && v.ph >= LPh.Tell;
  let tmx = 0, tmy = 0, tmz = 0;
  if (slapping) { toModel(v, v.tx, v.ty, v.tz, sv2); tmx = sv2.x; tmy = sv2.y; tmz = sv2.z; }
  for (let i = 0; i < N_LIMBS; i++) {
    const base = i * JOINTS * 4;
    idlePose(i, v.age, QI, 0, JA);
    let w = 0;
    if (slapping && i === v.limb) {
      // the pair of poses this phase blends between
      const k = clamp(v.prog, 0, 1);
      if (v.ph === LPh.Tell) {
        const th = aimPose(i, tmx, tmy, tmz, 1.05, QR, 0);
        for (let j = 1; j < JOINTS; j++) { JB[j * 2] = th; JB[j * 2 + 1] = 0; }
        w = smoothstep(0, 1, k);
        blend(QI, JA, QR, JB, w, out, base);
        continue;
      }
      if (v.ph === LPh.Exec) {
        const thr = aimPose(i, tmx, tmy, tmz, 1.05, QB, 0);
        const th = aimPose(i, tmx, tmy, tmz, 0, QR, 0);
        for (let j = 1; j < JOINTS; j++) { JA[j * 2] = thr; JA[j * 2 + 1] = 0; JB[j * 2] = th; JB[j * 2 + 1] = 0; }
        blend(QB, JA, QR, JB, k * k, out, base);
        continue;
      }
      const th = aimPose(i, tmx, tmy, tmz, 0, QR, 0);
      for (let j = 1; j < JOINTS; j++) { JB[j * 2] = th; JB[j * 2 + 1] = 0; }
      w = v.ph === LPh.Retract ? 1 - smoothstep(0, 1, k) : 1;
      blend(QI, JA, QR, JB, w, out, base);
      continue;
    }
    blend(QI, JA, QI, JA, 0, out, base);
  }
}

function blend(qa: Float64Array, ja: Float64Array, qb: Float64Array, jb: Float64Array, w: number, out: Float64Array, base: number) {
  qSlerp(qa, 0, qb, 0, w, out, base);
  for (let k = 1; k < JOINTS; k++) {
    const bx = ja[k * 2] + (jb[k * 2] - ja[k * 2]) * w;
    const bz = ja[k * 2 + 1] + (jb[k * 2 + 1] - ja[k * 2 + 1]) * w;
    // q = Rx(bx) ⊗ Rz(bz)
    const hx = bx * 0.5, hz = bz * 0.5;
    const sx = Math.sin(hx), cx = Math.cos(hx), sz = Math.sin(hz), cz = Math.cos(hz);
    const o = base + k * 4;
    out[o] = sx * cz; out[o + 1] = -sx * sz; out[o + 2] = cx * sz; out[o + 3] = cx * cz;
  }
}

/** Forward kinematics of limb i in model space: joint points p0..p12 (p12 = the head), then the
 *  gold core and the drill tip: 15 points × 3 into pts. */
export function limbFK(i: number, quats: Float64Array, pts: Float64Array) {
  const s = SOCKETS[i];
  let px = s[0], py = s[1], pz = s[2];
  const base = i * JOINTS * 4;
  QC[0] = quats[base]; QC[1] = quats[base + 1]; QC[2] = quats[base + 2]; QC[3] = quats[base + 3];
  pts[0] = px; pts[1] = py; pts[2] = pz;
  for (let k = 0; k < JOINTS; k++) {
    qRot(QC, 0, 0, -SEG_OFF[k], 0, sv);
    px += sv.x; py += sv.y; pz += sv.z;
    pts[(k + 1) * 3] = px; pts[(k + 1) * 3 + 1] = py; pts[(k + 1) * 3 + 2] = pz;
    if (k + 1 < JOINTS) { qMul(QC, 0, quats, base + (k + 1) * 4, QA, 0); QC.set(QA); }
  }
  qRot(QC, 0, 0, -CORE_OFF, 0, sv);
  pts[39] = px + sv.x; pts[40] = py + sv.y; pts[41] = pz + sv.z;
  qRot(QC, 0, 0, -HEAD_LEN, 0, sv);
  pts[42] = px + sv.x; pts[43] = py + sv.y; pts[44] = pz + sv.z;
  return pts;
}

// ------------------------------------------------------------------ the volumes (server + client)
export interface LeviHit { t: number; part: LPart; idx: number; av: number; mul: number; weak: boolean }
export const newLeviHit = (): LeviHit => ({ t: -1, part: LPart.None, idx: -1, av: 0, mul: 1, weak: false });
const LQ = new Float64Array(N_LIMBS * JOINTS * 4), LP = new Float64Array(45);

function sphereT(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, c: V3 | number[], r: number) {
  const lx = c[0] - ox, ly = c[1] - oy, lz = c[2] - oz;
  const tca = lx * dx + ly * dy + lz * dz;
  const d2 = lx * lx + ly * ly + lz * lz - tca * tca;
  if (d2 > r * r) return -1;
  const t0 = tca - Math.sqrt(r * r - d2);
  return t0 >= 0 ? t0 : tca >= 0 ? 0 : -1;
}
/** ray vs capsule (a → b, radius r): approximate entry distance or -1 */
function capsuleT(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, ax: number, ay: number, az: number, bx: number, by: number, bz: number, r: number) {
  const vx = bx - ax, vy = by - ay, vz = bz - az;
  const wx = ox - ax, wy = oy - ay, wz = oz - az;
  const b = dx * vx + dy * vy + dz * vz, c = vx * vx + vy * vy + vz * vz;
  const d = dx * wx + dy * wy + dz * wz, e = vx * wx + vy * wy + vz * wz;
  const den = c - b * b;
  let sc: number, tc: number;
  if (den < 1e-8) { sc = 0; tc = c > 0 ? clamp(e / c, 0, 1) : 0; }
  else { sc = (b * e - c * d) / den; tc = (e - b * d) / den; }
  if (tc < 0 || tc > 1) { tc = clamp(tc, 0, 1); sc = tc * b - d; }
  if (sc < 0) sc = 0;
  const qx = wx + dx * sc - vx * tc, qy = wy + dy * sc - vy * tc, qz = wz + dz * sc - vz * tc;
  const d2 = qx * qx + qy * qy + qz * qz;
  if (d2 > r * r) return -1;
  return Math.max(0, sc - Math.sqrt(r * r - d2));
}

/** What a hit on (part, idx) costs: armour, multiplier, gold. */
export function partArmor(v: LeviView, part: LPart, out: LeviHit) {
  out.av = 0; out.mul = 1; out.weak = false;
  switch (part) {
    case LPart.Hull: if (v.skinned) { out.av = 1; } else { out.av = 4; out.mul = 0.3; } break;
    case LPart.Eye: out.mul = 1.5; out.weak = true; break;
    case LPart.Lid: out.av = 4; out.mul = 0.3; break;
    case LPart.Maw: if (jawOpen(v) > 0.5) { out.mul = 1.4; out.weak = true; } else { out.av = 2; out.mul = 0.6; } break;
    case LPart.Jaw: out.av = 2; out.mul = 0.6; break;
    case LPart.Turret: case LPart.Tendril: case LPart.Head: out.av = 1; break;
    case LPart.Barnacle: break;
    case LPart.Crack: out.mul = 1.5; out.weak = true; break;
    case LPart.Socket: if (((v.socks >> out.idx) & 1) !== 0) { out.mul = 1.25; out.weak = true; } else { out.av = 1; } break;
    case LPart.Core: out.mul = 1.25; out.weak = true; break;
  }
  return out;
}

export const PART_LABEL = ['', 'HULL · AP5', 'EYE · CRIT', 'VISOR · AP5', 'MAW · AV2', 'JAW · AV2', 'TURRET · AV1', 'BARNACLE', 'WOUND · CRIT', 'STUMP · AV1', 'TENDRIL · AV1', 'DRILL HEAD · AV1', 'HEAD CORE · CRIT'];
export function partLabel(v: LeviView, h: LeviHit) {
  if (h.part === LPart.Hull && v.skinned) return 'CRACKED HULL · AV1';
  if (h.part === LPart.Maw && h.weak) return 'FURNACE · CRIT';
  if (h.part === LPart.Socket && h.weak) return 'OPEN SOCKET · CRIT';
  if (h.part === LPart.Turret) return TURRETS[h.idx]?.rocket ? 'ROCKET DRUM · AV1' : 'BLASTER TURRET · AV1';
  return PART_LABEL[h.part];
}

const mo = { x: 0, y: 0, z: 0 };
/** Ray (world, unit dir) against the whole creature. t is in world metres; -1 = miss. */
export function leviRaycast(v: LeviView, ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, maxT: number, out: LeviHit) {
  out.t = -1; out.part = LPart.None; out.idx = -1;
  // into model space (scale out), direction rotated only
  toModel(v, ox, oy, oz, mo);
  const lox = mo.x, loy = mo.y, loz = mo.z;
  const a = v.yaw + Math.PI, c = Math.cos(a), s = Math.sin(a);
  const ldx = c * dx - s * dz, ldz = s * dx + c * dz, ldy = dy;
  const S = LEVI_SCALE;
  let best = maxT / S;
  let bestPart = LPart.None, bestIdx = -1;
  const take = (t: number, part: LPart, idx: number) => { if (t >= 0 && t < best) { best = t; bestPart = part; bestIdx = idx; } };
  // the hull: a part sitting on its skin wins within BIAS of the hull's own hit
  const BIAS = 4;
  let tHull = -1;
  for (const h of HULL) { const t = sphereT(lox, loy, loz, ldx, ldy, ldz, h.c, h.r); if (t >= 0 && (tHull < 0 || t < tHull)) tHull = t; }
  const surf = (t: number) => (t < 0 ? -1 : tHull >= 0 && t > tHull + BIAS ? -1 : Math.max(0, Math.min(t, tHull >= 0 ? tHull : t) - 0.01));
  if (tHull >= 0) {
    for (let i = 0; i < 2; i++) {
      const e = EYES[i];
      const t = sphereT(lox, loy, loz, ldx, ldy, ldz, [e.p[0] + e.ax[0] * 1.2, e.p[1] + e.ax[1] * 1.2, e.p[2] + e.ax[2] * 1.2], 5.6);
      take(surf(t), (v.lids & (1 << i)) ? LPart.Lid : LPart.Eye, i);
    }
    take(surf(sphereT(lox, loy, loz, ldx, ldy, ldz, MAW, 7.5)), LPart.Maw, 0);
    take(surf(sphereT(lox, loy, loz, ldx, ldy, ldz, JAW, 5.5)), LPart.Jaw, 0);
    for (let i = 0; i < N_TURRETS; i++) {
      if (!turretAlive(v.parts, i)) continue;
      const tr = TURRETS[i], h = tr.rocket ? 4.5 : 5.5;
      take(surf(sphereT(lox, loy, loz, ldx, ldy, ldz, [tr.p[0] + tr.ax[0] * h, tr.p[1] + tr.ax[1] * h, tr.p[2] + tr.ax[2] * h], 5.5)), LPart.Turret, i);
    }
    for (let i = 0; i < N_BARN; i++) {
      if (!barnAlive(v.parts, i)) continue;
      const b = BARNACLES[i];
      take(surf(sphereT(lox, loy, loz, ldx, ldy, ldz, [b[0], b[1] - 1, b[2]], 3.8)), LPart.Barnacle, i);
    }
    if (v.skinned) for (let i = 0; i < WOUND_CRACKS.length; i++) take(surf(sphereT(lox, loy, loz, ldx, ldy, ldz, WOUND_CRACKS[i], 4.6)), LPart.Crack, i);
    for (let i = 0; i < N_LIMBS; i++) if (!limbAlive(v.parts, i)) take(surf(sphereT(lox, loy, loz, ldx, ldy, ldz, SOCKETS[i], 5.2)), LPart.Socket, i);
    if (bestPart === LPart.None && tHull < best) { best = tHull; bestPart = LPart.Hull; bestIdx = 0; }
  }
  // the tendrils hang free of the hull: nearest wins
  poseLimbs(v, LQ);
  for (let i = 0; i < N_LIMBS; i++) {
    if (!limbAlive(v.parts, i)) continue;
    limbFK(i, LQ, LP);
    // cheap reject: the whole limb inside a sphere round its middle joint
    const mx = LP[18], my = LP[19], mz = LP[20];
    if (sphereT(lox, loy, loz, ldx, ldy, ldz, [mx, my, mz], REACH * 0.62) < 0) continue;
    take(sphereT(lox, loy, loz, ldx, ldy, ldz, [LP[39], LP[40], LP[41]], 2.6) - 0.6, LPart.Core, i);
    take(capsuleT(lox, loy, loz, ldx, ldy, ldz, LP[36], LP[37], LP[38], LP[42], LP[43], LP[44], 2.7), LPart.Head, i);
    for (let k = 0; k < JOINTS; k++) {
      const p = k * 3, q = (k + 1) * 3;
      take(capsuleT(lox, loy, loz, ldx, ldy, ldz, LP[p], LP[p + 1], LP[p + 2], LP[q], LP[q + 1], LP[q + 2], SEG_R[k] + 0.4), LPart.Tendril, i);
    }
  }
  if (bestPart === LPart.None) return out;
  out.t = best * S; out.part = bestPart; out.idx = bestIdx;
  partArmor(v, bestPart, out);
  return out;
}

// ------------------------------------------------------------------ server state
export interface LeviState extends BossBase {
  kind: BossKind.Leviathan;
  /** ticks since it was summoned */
  age: number;
  rise: number;
  /** station anchor (hull centre at rest), the islet it guards */
  sx: number; sy: number; sz: number; ix: number; iz: number; deck: number;
  atk: LAtk; ph: LPh; phT: number; phDur: number; gap: number; cycle: number;
  limb: number; tx: number; ty: number; tz: number; hitMask: number;
  aims: { x: number; y: number; z: number }[];
  fireT: number; fireRR: number;
  lidT: Int16Array; sockT: Int16Array;
  /** the cowboy its eyes are on (pid, -1 none) */
  target: number;
}

export interface LeviNet extends BossNet {
  kind: BossKind.Leviathan;
  age: number; parts: number; lids: number; socks: number; limb: number;
  tx: number; ty: number; tz: number;
  /** 0..1 through the current phase; 0..1 through the rise */
  prog: number; rise: number;
  target: number;
}

/** The run-long wound (§4b cumulative): survives every extraction until startRun resets it. */
interface Wound { hp: number; max: number; parts: number; limb: number[]; turret: number[]; barn: number[]; dead: boolean; visits: number }
const WOUNDS = new WeakMap<Game, Wound>();
function wound(g: Game): Wound {
  let w = WOUNDS.get(g);
  if (!w) {
    const n = Math.max(1, g.players.size), k = 1 + 0.35 * (n - 1);
    w = { hp: LEVI_HP * k, max: LEVI_HP * k, parts: ALL_PARTS, limb: Array(N_LIMBS).fill(LIMB_HP * k), turret: Array(N_TURRETS).fill(TURRET_HP * k), barn: Array(N_BARN).fill(BARNACLE_HP * k), dead: false, visits: 0 };
    WOUNDS.set(g, w);
  }
  return w;
}
/** A fresh run: a fresh, whole Leviathan (sim/run.ts startRun). */
export function resetLeviathan(g: Game) { WOUNDS.delete(g); }
/** The extraction's robot cap with the Leviathan here (it brings its own pressure: G.U.L.P. sends
 *  fewer robots), and none at all once the cycle is broken. */
export function pursuerCap(g: Game, cap: number) {
  const w = WOUNDS.get(g);
  if (w?.dead) return 0;
  return g.boss?.kind === BossKind.Leviathan ? cap - 3 : cap;
}
export const leviathanBroken = (g: Game) => !!WOUNDS.get(g)?.dead;

/** sim/run.ts RunPhase.ExtractHoldout / ExtractBoard (not imported: run.ts imports this module, and
 *  the registry cycle would reach LEVIATHAN before it exists; tools/leviathan-test.ts pins the values) */
export const PH_HOLDOUT = 8, PH_BOARD = 9;
const aboard = (g: Game, pid: number) => (g.run.boardMask & (1 << (pid & 31))) !== 0;
function targets(g: Game) {
  const out: PlayerState[] = [];
  for (const p of g.players.values()) if (isTargetable(p) && p.phase === Phase.Alive && !aboard(g, p.id)) out.push(p);
  return out;
}
const body = (g: Game, s: LeviState) => g.enemies.find((e) => e.id === s.eid && !e.dead) ?? null;

/** The bell was rung: it comes. Called by sim/run.ts ringBell; no-op once the cycle is broken. */
export function summonLeviathan(g: Game) {
  const w = wound(g);
  if (w.dead || g.boss) return;
  const isl = g.world.extractIslet;
  let deck = g.world.terrain(isl.cx, isl.cz);
  if (deck === NO_GROUND) deck = isl.baseY;
  // beside the islet (off its flank, not between it and the main island or over the train), on
  // whichever side has more open sky between it and the other islands
  const ul = len2(isl.cx, isl.cz) || 1, ux = isl.cx / ul, uz = isl.cz / ul;
  let bx = 0, bz = 0, bestClear = -1e9;
  for (const side of [1, -1]) {
    const x = isl.cx - uz * side * STATION + ux * STATION_OUT, z = isl.cz + ux * side * STATION + uz * STATION_OUT;
    let clear = 1e9;
    for (const o of g.world.islands) if (o !== isl) clear = Math.min(clear, len2(x - o.cx, z - o.cz) - o.radius);
    if (clear > bestClear) { bestClear = clear; bx = x; bz = z; }
  }
  // positions ride the wire as int16 centimetres
  bx = clamp(bx, -300, 300); bz = clamp(bz, -300, 300);
  const e = g.spawnEnemy(EType.Leviathan, bx, bz);
  const sy = deck + HOVER;
  e.maxHp = w.max; e.hp = w.hp; e.armor = 4; e.st = EState.Chase;
  e.yaw = Math.atan2(-(isl.cx - bx), -(isl.cz - bz));
  e.y = sy - RISE_DEPTH;
  for (let k = 0; k < e.hx.length; k++) { e.hx[k] = e.x; e.hy[k] = e.y; e.hz[k] = e.z; e.hyaw[k] = e.yaw; }
  const s: LeviState = {
    kind: BossKind.Leviathan, eid: e.id, age: 0, rise: secs(RISE_SECS),
    sx: bx, sy, sz: bz, ix: isl.cx, iz: isl.cz, deck,
    atk: LAtk.None, ph: LPh.Idle, phT: 0, phDur: 1, gap: secs(1.5), cycle: 0,
    limb: -1, tx: 0, ty: 0, tz: 0, hitMask: 0, aims: [], fireT: secs(2), fireRR: 0,
    lidT: new Int16Array(2), sockT: new Int16Array(N_LIMBS), target: -1,
  };
  w.visits++;
  g.boss = s;
  g.emit(Ev.Boss, [e.id, BossBeat.Intro, 0, 0]);
  g.emit(Ev.Notice, [], LEVIATHAN.recall + (w.visits > 1 && w.hp < w.max ? ' IT REMEMBERS YOU.' : ''));
}

function viewOf(g: Game, s: LeviState, e: Enemy, tick: number, out: LeviView) {
  historyAt(e, tick, g.tick, hp);
  out.x = hp.x; out.y = hp.y; out.z = hp.z; out.yaw = hp.yaw;
  out.age = Math.max(0, s.age - (g.tick - tick)) / 60;
  const w = wound(g);
  out.parts = w.parts;
  out.lids = (s.lidT[0] > 0 ? 1 : 0) | (s.lidT[1] > 0 ? 2 : 0);
  out.socks = 0;
  for (let i = 0; i < N_LIMBS; i++) if (s.sockT[i] > 0) out.socks |= 1 << i;
  out.skinned = (w.parts & 31) === 0;
  out.atk = s.atk; out.ph = s.ph; out.prog = phaseProg(s);
  out.limb = s.limb; out.tx = s.tx; out.ty = s.ty; out.tz = s.tz;
  return out;
}
const phaseProg = (s: LeviState) => (s.atk === LAtk.None ? 0 : clamp(1 - s.phT / Math.max(1, s.phDur), 0, 1));

const hp = { x: 0, y: 0, z: 0, yaw: 0 };
const VW = newLeviView();
const LH = newLeviHit();
const wp = { x: 0, y: 0, z: 0 };

export const LEVIATHAN: BossDef<LeviState> = {
  kind: BossKind.Leviathan,
  name: 'THE LEVIATHAN',
  recall: 'SOMETHING VAST IS RISING OFF THE PLATFORM. G.U.L.P. CLASSIFIES IT AS WEATHER. HOLD THE PLATFORM.',
  types: [EType.Leviathan],
  procs: false,

  // Not an island boss: summonLeviathan() places it. This keeps the registry contract (a lab or a
  // stray devBoss still gets a working creature), stationed off the arena instead of in it.
  spawn(g, x, z) {
    summonLeviathan(g);
    const s = g.boss as LeviState | null;
    if (s && s.kind === BossKind.Leviathan) return s;
    const e = g.spawnEnemy(EType.Leviathan, x + STATION, z);
    return {
      kind: BossKind.Leviathan, eid: e.id, age: 0, rise: secs(RISE_SECS), sx: e.x, sy: e.y + HOVER, sz: e.z, ix: x, iz: z, deck: e.y,
      atk: LAtk.None, ph: LPh.Idle, phT: 0, phDur: 1, gap: secs(1.5), cycle: 0, limb: -1, tx: 0, ty: 0, tz: 0, hitMask: 0, aims: [],
      fireT: secs(2), fireRR: 0, lidT: new Int16Array(2), sockT: new Int16Array(N_LIMBS), target: -1,
    };
  },

  step(g, s) {
    const e = body(g, s);
    if (!e) return;
    if (!stepStatus(e, g)) return;
    stepLeviathan(g, s, e);
  },

  raycast(g, s, r: Ray, view, maxT, out: BossHit) {
    out.t = -1; out.e = null;
    const e = body(g, s);
    if (!e) return out;
    viewOf(g, s, e, view, VW);
    leviRaycast(VW, r.ox, r.oy, r.oz, r.dx, r.dy, r.dz, maxT, LH);
    if (LH.t < 0) return out;
    out.t = LH.t; out.e = e; out.part = LH.part; out.idx = LH.idx;
    out.av = LH.av; out.mul = LH.mul; out.weak = LH.weak; out.head = LH.weak;
    return out;
  },

  tookHit(g, s, hit, dealt) {
    const w = wound(g);
    const e = body(g, s);
    if (e) w.hp = Math.max(0, e.hp);
    switch (hit.part as LPart) {
      case LPart.Eye:
        if (s.lidT[hit.idx] <= 0) g.emit(Ev.Boss, [s.eid, LeviEv.Flinch, hit.idx, 0]);
        s.lidT[hit.idx] = secs(1.4);
        break;
      case LPart.Turret:
        if ((w.turret[hit.idx] -= dealt) <= 0 && turretAlive(w.parts, hit.idx)) losePart(g, s, LKind.Turret, hit.idx);
        break;
      case LPart.Barnacle:
        if ((w.barn[hit.idx] -= dealt) <= 0 && barnAlive(w.parts, hit.idx)) losePart(g, s, LKind.Barnacle, hit.idx);
        break;
      case LPart.Tendril: case LPart.Head: case LPart.Core:
        if ((w.limb[hit.idx] -= dealt) <= 0 && limbAlive(w.parts, hit.idx)) losePart(g, s, LKind.Limb, hit.idx);
        break;
    }
  },

  died(g, s) {
    const w = wound(g);
    w.dead = true; w.hp = 0;
    g.boss = null;
    // the robots rout: the god that sent them is dead
    for (const o of g.enemies) if (!o.dead && o.id !== s.eid) g.killEnemy(o, -1);
    g.emit(Ev.Notice, [], 'THE LEVIATHAN IS DOWN. THE CYCLE IS BROKEN. G.U.L.P. WILL BE IN TOUCH ABOUT THE DAMAGES.');
    return true;
  },

  note(_s, kind, a) {
    if (kind === LeviEv.Skinned) return 'EVERY TENDRIL IS GONE. THE HULL IS CRACKING OPEN.';
    if (kind === LeviEv.PartLost && a === LKind.Limb) return 'A TENDRIL IS SEVERED. ITS SOCKET IS OPEN: SHOOT THE WOUND.';
    return undefined;
  },

  enc(w: Writer, g, s) {
    const wd = wound(g);
    w.u16w(s.eid); w.u8w(s.atk);
    let socks = 0;
    for (let i = 0; i < N_LIMBS; i++) if (s.sockT[i] > 0) socks |= 1 << i;
    const bits = s.ph | ((wd.parts & 31) === 0 ? LBit.Skinned : 0) | (s.rise > 0 ? LBit.Rising : 0);
    w.u8w(bits);
    w.u8w(Math.round(phaseProg(s) * 255));
    w.u8w(s.rise > 0 ? Math.round((1 - s.rise / secs(RISE_SECS)) * 255) : 255);
    w.u16w(s.age & 0xffff);
    w.u32w(wd.parts);
    w.u8w((s.lidT[0] > 0 ? 1 : 0) | (s.lidT[1] > 0 ? 2 : 0) | (socks << 2));
    w.u8w(s.target < 0 ? 255 : s.target);
    if (s.atk === LAtk.Slap) { w.u8w(s.limb); w.cmw(s.tx); w.cmw(s.ty); w.cmw(s.tz); } else w.u8w(255);
  },

  dec(r: Reader): LeviNet {
    const eid = r.u16(), atk = r.u8(), phase = r.u8(), prog = r.u8() / 255, rise = r.u8() / 255, age = r.u16(), parts = r.u32();
    const ls = r.u8(), target = r.u8();
    const limb = r.u8();
    let tx = 0, ty = 0, tz = 0;
    if (limb !== 255) { tx = r.cm(); ty = r.cm(); tz = r.cm(); }
    return {
      kind: BossKind.Leviathan, eid, atk, phase, prog, rise, age, parts, lids: ls & 3, socks: ls >> 2,
      target: target === 255 ? -1 : target, limb: limb === 255 ? -1 : limb, tx, ty, tz,
    };
  },
};

// ------------------------------------------------------------------ the brain
function losePart(g: Game, s: LeviState, kind: LKind, i: number) {
  const w = wound(g);
  const bit = kind === LKind.Limb ? BIT_LIMB : kind === LKind.Turret ? BIT_TURRET : BIT_BARN;
  w.parts &= ~(1 << (bit + i));
  g.emit(Ev.Boss, [s.eid, LeviEv.PartLost, kind, i]);
  const note = LEVIATHAN.note!(s, LeviEv.PartLost, kind, i);
  if (kind === LKind.Limb) {
    s.sockT[i] = secs(4); // the socket wound window (§4b: strip, punish, reposition)
    if (s.atk === LAtk.Slap && s.limb === i) endAttack(g, s); // the slapping limb is gone: the slam dies with it
    if ((w.parts & 31) === 0) { g.emit(Ev.Boss, [s.eid, LeviEv.Skinned, 0, 0]); g.emit(Ev.Notice, [], LEVIATHAN.note!(s, LeviEv.Skinned, 0, 0)!); return; }
  }
  if (note) g.emit(Ev.Notice, [], note);
}

function stepLeviathan(g: Game, s: LeviState, e: Enemy) {
  s.age++;
  const w = wound(g);
  w.hp = Math.max(0, e.hp);
  for (let i = 0; i < 2; i++) if (s.lidT[i] > 0) s.lidT[i]--;
  for (let i = 0; i < N_LIMBS; i++) if (s.sockT[i] > 0) s.sockT[i]--;
  const ts = targets(g);
  // its eyes (and its whole bulk) settle on the nearest cowboy
  let near: PlayerState | null = null, nd = 1e9;
  for (const p of ts) { const d = len2(p.x - e.x, p.z - e.z); if (d < nd) { nd = d; near = p; } }
  s.target = near ? near.id : -1;
  // ---- the body: rise, then hold station with a slow drift and bob, swinging round to the posse
  const t = s.age / 60;
  const arc = Math.sin(t * 0.07) * 0.1;
  const ax = s.sx - s.ix, az = s.sz - s.iz, ca = Math.cos(arc), sa = Math.sin(arc);
  e.x = s.ix + ax * ca - az * sa; e.z = s.iz + ax * sa + az * ca;
  let y = s.sy + Math.sin(t * 0.4) * BOB;
  if (s.rise > 0) {
    const k = 1 - --s.rise / secs(RISE_SECS);
    y -= RISE_DEPTH * (1 - smoothstep(0, 1, k));
    if (s.rise === 0) { g.emit(Ev.Boss, [s.eid, BossBeat.Intro, 1, 0]); s.gap = secs(1.2); }
  }
  e.y = y; e.vx = e.vy = e.vz = 0; e.kx = e.kz = 0;
  const lx = near ? near.x : s.ix, lz = near ? near.z : s.iz;
  const want = Math.atan2(-(lx - e.x), -(lz - e.z));
  e.yaw = wrapAngle(e.yaw + clamp(wrapAngle(want - e.yaw), -TURN * DT, TURN * DT));
  if (s.rise > 0) return;
  // ---- only while the posse holds the platform: once the train has gone it just watches
  const ph = g.run.phase;
  if (ph !== PH_HOLDOUT && ph !== PH_BOARD) { if (s.atk !== LAtk.None) endAttack(g, s); return; }
  stepAttack(g, s, e, ts);
  if (s.ph !== LPh.Tell && s.ph !== LPh.Recover) plink(g, s, e, ts); // G1: minor pressure pauses for the tells and recoveries
}

function rngFor(g: Game, s: LeviState) { return new Rng(hash32(g.seed, g.tick, s.eid, 0x1e71)); }

function stepAttack(g: Game, s: LeviState, e: Enemy, ts: PlayerState[]) {
  if (s.atk === LAtk.None) {
    if (--s.gap > 0) return;
    pickAttack(g, s, e, ts);
    return;
  }
  if (--s.phT > 0) return;
  const d = ATK_SECS[s.atk];
  switch (s.ph) {
    case LPh.Tell:
      execute(g, s, e);
      setPh(s, LPh.Exec, d[1]);
      break;
    case LPh.Exec:
      if (s.atk === LAtk.Slap) slam(g, s);
      setPh(s, LPh.Recover, d[2]);
      break;
    case LPh.Recover:
      if (d[3] > 0) { setPh(s, LPh.Retract, d[3]); break; }
      endAttack(g, s);
      break;
    default:
      endAttack(g, s);
  }
}
function setPh(s: LeviState, ph: LPh, sec: number) { s.ph = ph; s.phDur = s.phT = Math.max(1, secs(sec)); }
function endAttack(g: Game, s: LeviState) {
  s.atk = LAtk.None; s.ph = LPh.Idle; s.phT = 0; s.limb = -1; s.aims = [];
  // G3's movement beat, then the next action (cadence quickens as it bleeds)
  const w = wound(g);
  const hurt = 1 - w.hp / Math.max(1, w.max);
  s.gap = secs(rngFor(g, s).range(1.4, 2.6) * (1 - 0.3 * hurt));
}

const ORDER = [LAtk.Slap, LAtk.Barrage, LAtk.Belch];
function pickAttack(g: Game, s: LeviState, e: Enemy, ts: PlayerState[]) {
  const w = wound(g);
  const view = viewOf(g, s, e, g.tick, VW);
  const inRange = ts.filter((p) => Math.hypot(p.x - e.x, p.y - e.y, p.z - e.z) < LOB_RANGE);
  if (!inRange.length) { s.gap = secs(0.5); return; }
  const rng = rngFor(g, s);
  for (let n = 0; n < ORDER.length; n++) {
    const atk = ORDER[(s.cycle + n) % ORDER.length];
    if (atk === LAtk.Slap) {
      // the nearest living limb that can reach a cowboy standing on something
      let bestL = -1, bestP: PlayerState | null = null, bd = 1e9;
      for (const p of inRange) {
        const gy = g.world.terrain(p.x, p.z);
        if (gy === NO_GROUND || p.y - gy > 3) continue;
        toModel(view, p.x, gy, p.z, wp);
        for (let i = 0; i < N_LIMBS; i++) {
          if (!limbAlive(w.parts, i)) continue;
          const sk = SOCKETS[i], dd = Math.hypot(wp.x - sk[0], wp.y - sk[1], wp.z - sk[2]);
          if (dd > REACH * 0.95 || dd < REACH * 0.4) continue;
          if (dd < bd) { bd = dd; bestL = i; bestP = p; }
        }
      }
      if (bestL < 0 || !bestP) continue;
      const gy = g.world.terrain(bestP.x, bestP.z);
      s.limb = bestL; s.tx = bestP.x; s.ty = gy; s.tz = bestP.z; s.hitMask = 0;
    } else if (atk === LAtk.Barrage) {
      if (!turretAlive(w.parts, 4) && !turretAlive(w.parts, 5)) continue;
      s.aims = [];
      for (const p of inRange.slice(0, 2)) {
        const a0 = rng.range(0, Math.PI * 2);
        for (let k = 0; k < 3; k++) {
          const a = a0 + k * 2.1, r = k === 0 ? rng.range(0, 1.5) : rng.range(3.6, 5.5);
          s.aims.push({ x: p.x + Math.cos(a) * r, y: p.y, z: p.z + Math.sin(a) * r });
        }
      }
    } else {
      s.aims = [];
      for (let k = 0; k < 3; k++) {
        const p = inRange[k % inRange.length];
        const a = rng.range(0, Math.PI * 2), r = k === 0 ? 0.5 : rng.range(3, 6);
        s.aims.push({ x: p.x + Math.cos(a) * r, y: p.y, z: p.z + Math.sin(a) * r });
      }
    }
    s.cycle = (s.cycle + n + 1) % ORDER.length;
    s.atk = atk;
    setPh(s, LPh.Tell, ATK_SECS[atk][0]);
    g.emit(Ev.Boss, [s.eid, BossBeat.Attack, atk, atk === LAtk.Slap ? s.limb : 0]);
    return;
  }
  s.gap = secs(0.5);
}

/** a model-space point → world */
function worldOf(g: Game, s: LeviState, e: Enemy, m: V3) {
  VW.x = e.x; VW.y = e.y; VW.z = e.z; VW.yaw = e.yaw;
  return toWorld(VW, m[0], m[1], m[2], wp);
}

function execute(g: Game, s: LeviState, e: Enemy) {
  const w = wound(g);
  if (s.atk === LAtk.Barrage) {
    // alternate the living drums; every shell is a lob with a bomb marker (the ground channel)
    const drums = [4, 5].filter((i) => turretAlive(w.parts, i));
    s.aims.forEach((a, k) => {
      const m = worldOf(g, s, e, TURRETS[drums[k % drums.length]].mz);
      const flight = 2.3 + k * 0.12;
      const v = lobVelocity(m.x, m.y, m.z, a.x, a.y, a.z, flight, BULLETS[BKind.Bomb].gravity);
      g.fireBullet(BKind.Bomb, m.x, m.y, m.z, v.vx, v.vy, v.vz, s.eid);
    });
    g.emit(Ev.Boss, [s.eid, LeviEv.Volley, LAtk.Barrage, 0]);
  } else if (s.atk === LAtk.Belch) {
    s.aims.forEach((a, k) => {
      const m = worldOf(g, s, e, SOCK_MAW);
      const v = lobVelocity(m.x, m.y, m.z, a.x, a.y, a.z, 2.7 + k * 0.2, BULLETS[BKind.Boulder].gravity);
      g.fireBullet(BKind.Boulder, m.x, m.y, m.z, v.vx, v.vy, v.vz, s.eid);
    });
    g.emit(Ev.Boss, [s.eid, LeviEv.Volley, LAtk.Belch, 0]);
  }
}

/** the slam lands: one hit per cowboy, shoved INBOARD (toward the islet centre), never off */
function slam(g: Game, s: LeviState) {
  for (const p of targets(g)) {
    const bit = 1 << (p.id & 31);
    if (s.hitMask & bit) continue;
    const d = len2(p.x - s.tx, p.z - s.tz);
    if (d > SLAP_R || Math.abs(p.y - s.ty) > 4) continue;
    s.hitMask |= bit;
    let kx = s.ix - p.x, kz = s.iz - p.z;
    const kl = len2(kx, kz) || 1;
    kx /= kl; kz /= kl;
    g.hurtPlayer(p, SLAP_DMG, s.tx, s.tz, 0, kx * SLAP_SHOVE, kz * SLAP_SHOVE);
  }
  g.emit(Ev.Boss, [s.eid, LeviEv.Slam, s.limb, 0]);
  g.emit(Ev.Explosion, [s.tx, s.ty + 0.5, s.tz, SLAP_R, 3]);
}

/** minor pressure: the front blaster turrets plink slow bolts at whoever is in range */
function plink(g: Game, s: LeviState, e: Enemy, ts: PlayerState[]) {
  if (--s.fireT > 0) return;
  s.fireT = secs(0.8);
  const w = wound(g);
  for (let n = 0; n < 4; n++) {
    const i = (s.fireRR + n) % 4;
    if (!turretAlive(w.parts, i)) continue;
    const m = worldOf(g, s, e, TURRETS[i].mz);
    const mx = m.x, my = m.y, mz = m.z;
    // the turret's facing (its mount normal, in the world)
    const ax = TURRETS[i].ax;
    const c = Math.cos(e.yaw + Math.PI), sn = Math.sin(e.yaw + Math.PI);
    const nx = ax[0] * c + ax[2] * sn, nz = -ax[0] * sn + ax[2] * c, ny = ax[1];
    for (const p of ts) {
      const dx = p.x - mx, dy = p.y + 1.1 - my, dz = p.z - mz, l = Math.hypot(dx, dy, dz);
      if (l > BOLT_RANGE || l < 1 || (dx * nx + dy * ny + dz * nz) / l < 0.05) continue;
      const sp = BULLETS[BKind.Pellet].speed;
      g.fireBullet(BKind.Pellet, mx, my, mz, (dx / l) * sp, (dy / l) * sp, (dz / l) * sp, s.eid);
      s.fireRR = (i + 1) % 4;
      return;
    }
  }
}
